import {
  JobStatus,
  type DataMovementJob,
} from '@wix/bex-utils/@wix/ambassador-hub-v1-job/types';

export const JOB_POLL_INTERVAL_MS = 2000;

/** A job is terminal once it has succeeded, partially succeeded, or failed. */
export function isTerminalJobStatus(status?: JobStatus): boolean {
  return (
    status === JobStatus.COMPLETED ||
    status === JobStatus.PARTIALLY_SUCCESSFUL ||
    status === JobStatus.FAILED
  );
}

export interface PollImportJobOptions {
  intervalMs?: number;
  /** Called with every fetched job, including the terminal one. */
  onProgress?: (job: DataMovementJob) => void;
  /** Polling stops and resolves `null` as soon as this returns true. */
  isCancelled?: () => boolean;
  /** Overridable wait between polls (tests inject a controllable timer). */
  wait?: (ms: number) => Promise<void>;
}

/**
 * Polls a data-movement job until it reaches a terminal status, then resolves
 * with the terminal job. Resolves `null` if cancelled mid-poll. Pure async unit
 * (no MobX) so `ImportState` stays thin and this is testable on its own.
 */
export async function pollImportJob(
  getJob: (jobId: string) => Promise<DataMovementJob>,
  jobId: string,
  options: PollImportJobOptions = {},
): Promise<DataMovementJob | null> {
  const {
    intervalMs = JOB_POLL_INTERVAL_MS,
    onProgress,
    isCancelled,
    wait = defaultWait,
  } = options;

  for (;;) {
    if (isCancelled?.()) {
      return null;
    }

    const job = await getJob(jobId);
    onProgress?.(job);

    if (isTerminalJobStatus(job.status)) {
      return job;
    }

    if (isCancelled?.()) {
      return null;
    }

    await wait(intervalMs);
  }
}

function defaultWait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
